/**
 * Notes — searchable, multi-document personal notepad for every Hines Time user.
 *
 * Workers can:
 *   - Create / edit / delete notes (soft delete)
 *   - Optionally link a note to one of their active jobs (dropdown)
 *   - Search by title / body / tags
 *   - Send a note to any coworker (creates a copy in their inbox + SMS)
 *
 * Designed to feel like Apple Notes: master list on the left, editor on the right.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Trash2, Send, FileText, Briefcase, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState({ title: '', body: '', job_id: '', tags: [] });
  const [saving, setSaving] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [coverMsg, setCoverMsg] = useState('');

  const active = useMemo(() => notes.find((n) => n.id === activeId), [notes, activeId]);

  const load = useCallback(async (q = '') => {
    try {
      const [n, j, c] = await Promise.all([
        axios.get(`${API}/notes`, { headers: authHeaders(), params: q ? { q } : {} }),
        axios.get(`${API}/jobs`, { headers: authHeaders() }),
        axios.get(`${API}/notes/contacts/list`, { headers: authHeaders() }),
      ]);
      setNotes(n.data || []);
      setJobs((j.data || []).filter((x) => x.status !== 'completed'));
      setContacts(c.data?.contacts || []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load notes');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search, load]);

  useEffect(() => {
    if (active) {
      setDraft({
        title: active.title || '',
        body: active.body || '',
        job_id: active.job_id || '',
        tags: active.tags || [],
      });
    } else {
      setDraft({ title: '', body: '', job_id: '', tags: [] });
    }
  }, [active]);

  const newNote = async () => {
    try {
      const r = await axios.post(`${API}/notes`, { title: 'Untitled note', body: '' }, { headers: authHeaders() });
      setNotes((prev) => [r.data, ...prev]);
      setActiveId(r.data.id);
    } catch (err) {
      console.error(err);
      toast.error('Could not create note');
    }
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const payload = { ...draft, job_id: draft.job_id || null };
      const r = await axios.put(`${API}/notes/${active.id}`, payload, { headers: authHeaders() });
      setNotes((prev) => prev.map((n) => (n.id === active.id ? r.data : n)));
      toast.success('Saved');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!active) return;
    if (!window.confirm(`Delete "${active.title}"?`)) return;
    try {
      await axios.delete(`${API}/notes/${active.id}`, { headers: authHeaders() });
      setNotes((prev) => prev.filter((n) => n.id !== active.id));
      setActiveId(null);
      toast.success('Deleted');
    } catch (err) {
      console.error(err);
      toast.error('Delete failed');
    }
  };

  const send = async () => {
    if (!active || !recipient) return;
    try {
      await save();
      await axios.post(`${API}/notes/send`, {
        note_id: active.id,
        recipient_user_id: recipient,
        cover_message: coverMsg || null,
      }, { headers: authHeaders() });
      toast.success('Note delivered');
      setSendDialog(false);
      setRecipient('');
      setCoverMsg('');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to send');
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="notes-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" /> Notes
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Searchable scratchpad. Link a note to an active job, or send a copy to a teammate.
          </p>
        </div>
        <Button onClick={newNote} data-testid="notes-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Note
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[60vh]">
        {/* Master list */}
        <Card className="p-3 md:col-span-1 max-h-[70vh] overflow-y-auto">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input
              placeholder="Search notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="notes-search"
            />
          </div>
          {notes.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-8 italic">
              {search ? 'No matches.' : 'No notes yet — create one to get started.'}
            </p>
          ) : (
            notes.map((n) => (
              <button
                key={n.id}
                onClick={() => setActiveId(n.id)}
                className={`w-full text-left p-2 rounded mb-1 transition ${
                  activeId === n.id
                    ? 'bg-blue-100 dark:bg-blue-900/40'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
                data-testid={`notes-list-item-${n.id}`}
              >
                <p className="font-medium text-sm truncate">{n.title || '(untitled)'}</p>
                <p className="text-xs text-slate-500 truncate">{(n.body || '').slice(0, 60)}</p>
                <p className="text-xs text-slate-400 mt-0.5">{formatDate(n.updated_at)}</p>
              </button>
            ))
          )}
        </Card>

        {/* Editor */}
        <Card className="p-4 md:col-span-2">
          {!active ? (
            <div className="h-full flex items-center justify-center text-slate-400">
              <p>Select a note or create a new one.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <Input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Note title"
                className="text-lg font-medium"
                data-testid="notes-title-input"
              />
              {active.from_user_id && (
                <p className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-950/30 rounded inline-block">
                  📩 From {active.from_user_name}
                </p>
              )}
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Briefcase className="w-3 h-3" />
                <span>Linked job:</span>
                <Select value={draft.job_id || 'none'} onValueChange={(v) => setDraft({ ...draft, job_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-7 w-64" data-testid="notes-job-link">
                    <SelectValue placeholder="No job linked" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(no job)</SelectItem>
                    {jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>{j.title || j.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                rows={16}
                placeholder="Write your note..."
                className="resize-none"
                data-testid="notes-body-textarea"
              />
              <div className="flex justify-between">
                <Button variant="ghost" onClick={remove} className="text-red-500">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSendDialog(true)} data-testid="notes-send-btn">
                    <Send className="w-4 h-4 mr-1" /> Send to teammate
                  </Button>
                  <Button onClick={save} disabled={saving} data-testid="notes-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={sendDialog} onOpenChange={setSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send note to a teammate</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Recipient</Label>
              <Select value={recipient} onValueChange={setRecipient}>
                <SelectTrigger><SelectValue placeholder="Choose a coworker..." /></SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cover message (optional)</Label>
              <Textarea
                value={coverMsg}
                onChange={(e) => setCoverMsg(e.target.value)}
                rows={3}
                placeholder="Just FYI — please read this before tomorrow..."
              />
            </div>
            <p className="text-xs text-slate-500">
              We'll create a copy in their inbox and text them a notification if they've opted in.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(false)}>Cancel</Button>
            <Button onClick={send} disabled={!recipient}>Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
